// Control Frame Characteristics
export const FLATNESS_SYMBOL = '⏥';
export const CYLINDRICITY_SYMBOL = '⌭';
export const PROFILE_OF_A_LINE_SYMBOL = '⌒';
export const PROFILE_OF_A_SURFACE_SYMBOL = '⌓';
export const PERPENDICULARITY_SYMBOL = '⏊';
export const ANGULARITY_SYMBOL = '∠';
export const POSITION_SYMBOL = '⌖';
export const CONCENTRICITY_SYMBOL = '◎';
export const RUNOUT_SYMBOL = '↗';
export const TOTAL_RUNOUT_SYMBOL = '⌰';
export const PARALLELISM_SYMBOL = '⫽';
export const SYMMETRY_SYMBOL = '⌯';
export const CIRCULARITY_SYMBOL = '○';
export const STRAIGHTNESS_SYMBOL = '⏤';

export const CONTROL_FRAME_CHARACTERISTICS = {
  [FLATNESS_SYMBOL]: 'Flatness',
  [CYLINDRICITY_SYMBOL]: 'Cylindricity',
  [PROFILE_OF_A_LINE_SYMBOL]: 'Profile of a Line',
  [PROFILE_OF_A_SURFACE_SYMBOL]: 'Profile of a Surface',
  [PERPENDICULARITY_SYMBOL]: 'Perpendicularity',
  [ANGULARITY_SYMBOL]: 'Angularity',
  [POSITION_SYMBOL]: 'Position',
  [CONCENTRICITY_SYMBOL]: 'Concentricity',
  [RUNOUT_SYMBOL]: 'Runout',
  [TOTAL_RUNOUT_SYMBOL]: 'Total Runout',
  [PARALLELISM_SYMBOL]: 'Parallelism',
  [SYMMETRY_SYMBOL]: 'Symmetry',
  [CIRCULARITY_SYMBOL]: 'Circularity',
  [STRAIGHTNESS_SYMBOL]: 'Straightness',
} as const;

export type ControlFrameCharacteristic =
  | typeof FLATNESS_SYMBOL
  | typeof CYLINDRICITY_SYMBOL
  | typeof PROFILE_OF_A_LINE_SYMBOL
  | typeof PROFILE_OF_A_SURFACE_SYMBOL
  | typeof PERPENDICULARITY_SYMBOL
  | typeof ANGULARITY_SYMBOL
  | typeof POSITION_SYMBOL
  | typeof CONCENTRICITY_SYMBOL
  | typeof RUNOUT_SYMBOL
  | typeof TOTAL_RUNOUT_SYMBOL
  | typeof PARALLELISM_SYMBOL
  | typeof SYMMETRY_SYMBOL
  | typeof CIRCULARITY_SYMBOL
  | typeof STRAIGHTNESS_SYMBOL;

export const RUNES_CALLOUT_TYPES = {
  DIM: 'dim',
  HOLE: 'hole',
  COUNTERBORE: 'counterbore',
  COUNTERSINK: 'countersink',
  DATUM: 'datum',
  CONTROL_FRAME: 'control_frame',
  COMPOSITE_CONTROL_FRAME: 'composite_control_frame',
  SURFACE_FINISH: 'surface_finish',
  BEND_LINE: 'bend_line',
  THREAD: 'thread',
  GENERAL_TOLERANCES_BLOCK: 'general_tolerances_block',
  FLAG_NOTE: 'flag_note',
  CAPTION: 'caption',
  WELD: 'weld',
  TABLE: 'table',
  CHAMFER: 'chamfer',
} as const;
export type RunesCalloutType =
  (typeof RUNES_CALLOUT_TYPES)[keyof typeof RUNES_CALLOUT_TYPES];

export const UNITS = {
  // preferred units
  IN: 'in',
  MM: 'mm',
  DEG: 'deg',
  GAUGE: 'gauge',
  // additional units
  CM: 'cm',
  M: 'm',
  FT: 'ft',
  DEG_SEC: 'deg_sec',
  DEG_MIN: 'deg_min',
} as const;
export type Units = (typeof UNITS)[keyof typeof UNITS];

// PartBotLocation type for callout positioning
export type PartBotLocation = [number, number, number, number]; // X1, Y1, X2, Y2 from bottom left origin

export type ConfidenceScore = number;

// Region types
export const RUNES_REGION_TYPES = {
  TITLE_BLOCK: 'title_block',
  NOTES_LIST: 'notes_list',
  VIEW_3D: 'view_3d',
  VIEW_2D: 'view_2d',
} as const;
export type RunesRegionType =
  (typeof RUNES_REGION_TYPES)[keyof typeof RUNES_REGION_TYPES];

export type TitleBlock = {
  regionType: typeof RUNES_REGION_TYPES.TITLE_BLOCK;
};

export type NotesList = {
  regionType: typeof RUNES_REGION_TYPES.NOTES_LIST;
};

export type View3D = {
  regionType: typeof RUNES_REGION_TYPES.VIEW_3D;
  embedding?: string | null;
  interestingness?: number | null;
};

export type View2D = {
  regionType: typeof RUNES_REGION_TYPES.VIEW_2D;
  embedding?: string | null;
  interestingness?: number | null;
};

export type RunesRegion = {
  region: TitleBlock | NotesList | View3D | View2D;
  loc: PartBotLocation;
  score: ConfidenceScore;
};

// Precision class enum
export const PRECISION_CLASSES = {
  FRACTION: 'fraction',
  INTEGER: 'integer',
  ONE_DECIMAL_PLACES: 'one_decimal_places',
  TWO_DECIMAL_PLACES: 'two_decimal_places',
  THREE_DECIMAL_PLACES: 'three_decimal_places',
  FOUR_DECIMAL_PLACES: 'four_decimal_places',
  FIVE_DECIMAL_PLACES: 'five_decimal_places',
} as const;
export type PrecisionClass =
  (typeof PRECISION_CLASSES)[keyof typeof PRECISION_CLASSES];

// Fraction type
export type Fraction = {
  wholeNumber?: number | null;
  numerator: number;
  denominator: number;
};

// RationalValue type
export type RationalValue = {
  numericValue: number | Fraction;
  units?: Units | null;
  precisionClass: PrecisionClass;
};

// MixedValue type
export type MixedValue = {
  mixedUnitValueComponents: RationalValue[];
};

// SimplifiedValue type
export type SimplifiedValue = {
  value: number;
  preferredUnits?: Units | null;
  precisionClass: PrecisionClass;
};

// Value type - updated to use MixedValue
export type Value = {
  value: MixedValue;
  altValue?: MixedValue | null;
  simplifiedValue: SimplifiedValue;
  simplifiedAltValue?: SimplifiedValue | null;
};

export const TOLERANCE_TYPES = {
  LIMIT: 'limit',
  SYMMETRIC: 'symmetric',
  ASYMMETRIC: 'asymmetric',
  MAX: 'max',
  MIN: 'min',
} as const;
export type ToleranceType =
  (typeof TOLERANCE_TYPES)[keyof typeof TOLERANCE_TYPES];

export type Tolerance = {
  tolType: ToleranceType;
  upper?: Value;
  lower?: Value;
};

export type Measurement = {
  value: Value;
  tol?: Tolerance;
};

export const DIMENSION_TYPES = {
  LINEAR: 'linear',
  ANGULAR: 'angular',
  DIAMETER: 'diameter',
  RADIUS: 'radius',
} as const;
export type DimensionType =
  (typeof DIMENSION_TYPES)[keyof typeof DIMENSION_TYPES];

export const ENCLOSURE_SHAPES = {
  CIRCLE: 'circle',
  BOX: 'box',
  OBROUND: 'obround',
  PENTAGON: 'pentagon',
  HEXAGON: 'hexagon',
  TRIANGLE: 'triangle',
  PARENTHESES: 'parentheses',
  OTHER: 'other',
} as const;
export type EnclosureShape =
  (typeof ENCLOSURE_SHAPES)[keyof typeof ENCLOSURE_SHAPES];

export const BEND_DIRECTIONS = {
  UP: 'up',
  DOWN: 'down',
} as const;
export type BendDirection =
  (typeof BEND_DIRECTIONS)[keyof typeof BEND_DIRECTIONS];

export const THREAD_SYSTEMS = {
  METRIC: 'metric',
  NPT: 'npt',
  UNC: 'unc',
  UNF: 'unf',
  UNEF: 'unef',
} as const;
export type ThreadSystem = (typeof THREAD_SYSTEMS)[keyof typeof THREAD_SYSTEMS];

export const HANDEDNESS = {
  LH: 'LH',
  RH: 'RH',
} as const;
export type Handedness = (typeof HANDEDNESS)[keyof typeof HANDEDNESS];

export const MATERIAL_REMOVAL_TYPES = {
  UNSPECIFIED: 'unspecified',
  REQUIRED: 'required',
  PROHIBITED: 'prohibited',
} as const;
export type MaterialRemovalType =
  (typeof MATERIAL_REMOVAL_TYPES)[keyof typeof MATERIAL_REMOVAL_TYPES];

export const VIEW_TYPES = {
  PROFILE_VIEW: 'profile_view',
  SECTION_VIEW: 'section_view',
  ISOMETRIC_VIEW: 'isometric_view',
  DETAIL_VIEW: 'detail_view',
} as const;
export type ViewType = (typeof VIEW_TYPES)[keyof typeof VIEW_TYPES];

export const ELEMENT_CLASSES = {
  TITLE_BLOCK: 'title_block',
  NOTES_LIST: 'notes_list',
  VIEW_3D: 'view_3d',
  VIEW_2D: 'view_2d',
  CAPTION: 'caption',
  TABLE: 'table',
  WELD: 'weld',
  GENERAL_TOLERANCES: 'general_tolerances',
  CALLOUT_REGION: 'callout_region',
} as const;
export type ElementClass =
  (typeof ELEMENT_CLASSES)[keyof typeof ELEMENT_CLASSES];

export const RUNES_API_VERSIONS = {
  V1_0_0: '1.0.0',
  V2_0_0: '2.0.0',
} as const;
export type RunesApiVersion =
  (typeof RUNES_API_VERSIONS)[keyof typeof RUNES_API_VERSIONS];

export const ORIENTATIONS = {
  NEAR_SIDE: 'near_side',
  FAR_SIDE: 'far_side',
  BOTH_SIDES: 'both_sides',
} as const;
export type Orientation = (typeof ORIENTATIONS)[keyof typeof ORIENTATIONS];

// Helper types
export type SpecificDecimalTolerance = {
  zeroPlaces?: number;
  onePlaces?: number;
  twoPlaces?: number;
  threePlaces?: number;
  fourPlaces?: number;
};

export type RawDetection = [ElementClass, PartBotLocation, ConfidenceScore];

export type Dimension = {
  calloutType: typeof RUNES_CALLOUT_TYPES.DIM;
  dimType: DimensionType;
  mult?: number | null;
  meas: Measurement;
  isOrigin: boolean;
  isBasic: boolean;
  isCtq: boolean;
  isRef: boolean;
  enclosureShape?: EnclosureShape | null;
  isTyp: boolean;
  isStk: boolean;
  isThk: boolean;
  orientation?: Orientation | null;
};

export const DEPTH_TYPES = {
  THRU: 'thru',
  DEPTH: 'depth',
} as const;
export type DepthType = (typeof DEPTH_TYPES)[keyof typeof DEPTH_TYPES];

export type Thread = {
  calloutType: typeof RUNES_CALLOUT_TYPES.THREAD;
  threadSystem: ThreadSystem;
  size: string;
  pitch?: string;
  threadDepth?: string;
  toleranceClass?: string;
  handedness?: Handedness;
  rawThreadStr: string;
};

export type Hole = {
  calloutType: typeof RUNES_CALLOUT_TYPES.HOLE;
  mult?: number | null;
  dia?: Measurement | null;
  depthType?: DepthType | null;
  depth?: Measurement | null;
  thread?: Thread | null;
  orientation?: Orientation | null;
  isTyp: boolean;
};

export type Countersink = {
  calloutType: typeof RUNES_CALLOUT_TYPES.COUNTERSINK;
  mult?: number | null;
  dia: Measurement;
  angle?: Measurement | null;
  orientation?: Orientation | null;
  isTyp: boolean;
};

export type Counterbore = {
  calloutType: typeof RUNES_CALLOUT_TYPES.COUNTERBORE;
  mult?: number | null;
  dia: Measurement;
  depthType?: DepthType | null;
  depth?: Measurement | null;
  thread?: Thread | null;
  orientation?: Orientation | null;
  isTyp: boolean;
};

export const MATERIAL_CONDITIONS = {
  MMC: 'mmc',
  LMC: 'lmc',
} as const;
export type MaterialCondition =
  (typeof MATERIAL_CONDITIONS)[keyof typeof MATERIAL_CONDITIONS];

export type Datum = {
  calloutType: typeof RUNES_CALLOUT_TYPES.DATUM;
};

export type DatumReference = {
  datum: string;
  matCon?: MaterialCondition;
};

export type FeatureControlFrame = {
  calloutType: typeof RUNES_CALLOUT_TYPES.CONTROL_FRAME;
  mult?: number | null;
  characteristic: ControlFrameCharacteristic;
  dimType: DimensionType;
  value: Value;
  matCon?: MaterialCondition | null;
  datums: DatumReference[];
};

export type CompositeControlFrame = {
  calloutType: typeof RUNES_CALLOUT_TYPES.COMPOSITE_CONTROL_FRAME;
  controlFrames: FeatureControlFrame[];
  rawTexts: string[];
};

export type BendLine = {
  calloutType: typeof RUNES_CALLOUT_TYPES.BEND_LINE;
  direction: BendDirection;
  angle: Measurement;
  radius?: Measurement;
};

export type Chamfer = {
  calloutType: typeof RUNES_CALLOUT_TYPES.CHAMFER;
  mult?: number | null;
  legLength: Measurement;
  angle?: Measurement | null;
  secondLegLength?: Measurement | null;
  isTyp?: boolean;
};

export type SurfaceFinish = {
  calloutType: typeof RUNES_CALLOUT_TYPES.SURFACE_FINISH;
  materialRemovalType: MaterialRemovalType;
  raMean?: number | null;
  raRange?: [number, number] | null;
  raClass?: string | null;
};

// ToleranceProcess enum
export const TOLERANCE_PROCESSES = {
  MACH: 'mach',
  BEND: 'bend',
  CHAMFER: 'chamfer',
  BREAK_EDGES: 'break_edges',
  FILLETS: 'fillets',
  PLATENUT: 'platenut',
  FORMED: 'formed',
  LOCATING: 'locating',
  SHEET_METAL: 'sheet_metal',
  CORNER_RADIUS: 'corner_radius',
  PRESS: 'press',
  BLANKED: 'blanked',
  DETAIL: 'detail',
  ASSEMBLY: 'assembly',
  CONCENTRIC: 'concentric',
  FABRICATION: 'fabrication',
  WELD: 'weld',
} as const;
export type ToleranceProcess =
  (typeof TOLERANCE_PROCESSES)[keyof typeof TOLERANCE_PROCESSES];

// GeneralTolerance type
export type GeneralTolerance = {
  generalPrecisionClass?: PrecisionClass | null;
  toleranceValue: Tolerance;
  toleranceProcess?: ToleranceProcess | null;
};

// GeneralTolerancesBlock type
export type GeneralTolerancesBlock = {
  calloutType: typeof RUNES_CALLOUT_TYPES.GENERAL_TOLERANCES_BLOCK;
  generalUnits?: Units | null;
  generalAltUnits?: Units | null;
  generalTolerances: GeneralTolerance[];
  generalRoughness?: SurfaceFinish | null;
  toleranceStandard?: string | null;
};

export type FlagNote = {
  calloutType: typeof RUNES_CALLOUT_TYPES.FLAG_NOTE;
  mult?: number | null;
  listMarkerValue: string;
  secondListMarkerValue?: string | null;
  enclosureShape: EnclosureShape;
};

export type Caption = {
  calloutType: typeof RUNES_CALLOUT_TYPES.CAPTION;
  viewType?: ViewType;
  region?: RunesRegion;
};

// WeldSymbol enum
export const WELD_SYMBOLS = {
  FILLET: 'fillet',
  PLUG: 'plug',
  SPOT: 'spot',
  STUD: 'stud',
  SEAM: 'seam',
  BACK: 'back',
  SURFACING: 'surfacing',
  EDGE: 'edge',
  SQUARE_GROOVE: 'square_groove',
  V_GROOVE: 'v_groove',
  BEVEL_GROOVE: 'bevel_groove',
  U_GROOVE: 'u_groove',
  J_GROOVE: 'j_groove',
  FLARE_V_GROOVE: 'flare_v_groove',
  FLARE_BEVEL_GROOVE: 'flare_bevel_groove',
  SCARF: 'scarf',
} as const;
export type WeldSymbol = (typeof WELD_SYMBOLS)[keyof typeof WELD_SYMBOLS];

// ContourFeature enum
export const CONTOUR_FEATURES = {
  FLAT: 'flat',
  CONCAVE: 'concave',
  CONVEX: 'convex',
} as const;
export type ContourFeature =
  (typeof CONTOUR_FEATURES)[keyof typeof CONTOUR_FEATURES];

// WeldSpec type
export type WeldSpec = {
  symbol: WeldSymbol;
  size?: Value | null;
  length?: Value | null;
  pitch?: Value | null;
  number?: number | null;
  finishing?: string | null;
  contourFeature?: ContourFeature | null;
  grooveDepth?: Value | null;
  grooveSize?: Value | null;
  grooveAngle?: Value | null;
  fillDepth?: Value | null;
  isFieldWeld: boolean;
  isMeltThru: boolean;
  isConsumableInsert: boolean;
  isSpacer: boolean;
};

// Weld type
export type Weld = {
  calloutType: typeof RUNES_CALLOUT_TYPES.WELD;
  arrowSide?: WeldSpec | null;
  otherSide?: WeldSpec | null;
  tail?: string | null;
  mult?: number | null;
  isAllAround: boolean;
};

export type RunesTable = {
  calloutType: typeof RUNES_CALLOUT_TYPES.TABLE;
};

export type CalloutType =
  | Dimension
  | Hole
  | Countersink
  | Counterbore
  | Datum
  | FeatureControlFrame
  | CompositeControlFrame
  | Thread
  | BendLine
  | Chamfer
  | SurfaceFinish
  | GeneralTolerancesBlock
  | FlagNote
  | Caption
  | Weld
  | RunesTable;

export type RunesCallout = {
  callout: CalloutType;
  rawText: string;
  loc: PartBotLocation;
  desc?: string | null;
  anomalous?: boolean;
};

export type CalloutGroup = {
  callouts: number[];
  loc: PartBotLocation;
};

export type RunesResponse = {
  callouts: RunesCallout[];
  calloutGroups: CalloutGroup[];
  regions: RunesRegion[];
  rawDetections: RawDetection[];
  version: string;
  stage1Ms: number;
  stage1GpuMs: number;
  stage2Ms: number;
};
